import { isDefined } from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ReconciliationFormOutput } from '../../modules/reconciliation/schemas.ts';
import { ReconciliationSheetFormOutput } from '../../modules/referenceView/ReconciliationSheet/schemas.ts';
import { ReconciliationState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { submitEditOrderDetailsForm } from './actions/submitEditOrderDetailsForm.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export const initialReconciliationState: ReconciliationState = {
  opinionOfMarketValue: undefined,
  includeTotalCostToCure: false,
  includeOpinionOfAfterRepairValue: false,
  includeTotalRenovationBudget: false,
  totalCostToCure: undefined,
  opinionOfAfterRepairValue: undefined,
  totalRenovationBudget: undefined,
  allowSubjectToValue: true,
  valueTypes: [],
};

const reconciliationSlice = createSlice({
  name: 'reconciliation',
  initialState: initialReconciliationState,
  reducers: {
    submitReconciliationDataSheet: (
      state,
      action: PayloadAction<ReconciliationSheetFormOutput>,
    ) => {
      const {
        opinionOfMarketValue,
        totalCostToCure,
        totalRenovationBudget,
        opinionOfAfterRepairValue,
      } = action.payload;

      Object.assign(state, {
        syncState: getBumpedSyncState(state.syncState),
        opinionOfMarketValue,
        totalCostToCure,
        totalRenovationBudget,
        opinionOfAfterRepairValue,
      });
    },
    submitReconciliation: (state, action: PayloadAction<ReconciliationFormOutput>) => {
      const {
        opinionOfMarketValue,
        totalCostToCure,
        opinionOfAfterRepairValue,
        totalRenovationBudget,
        valueTypes,
      } = action.payload;

      Object.assign(state, {
        opinionOfMarketValue,
        totalCostToCure,
        opinionOfAfterRepairValue,
        totalRenovationBudget,
        valueTypes,
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    setReconciliation: (state, action: PayloadAction<ReconciliationState>) => {
      let { valueTypes } = action.payload;
      if (!valueTypes.length) {
        valueTypes = action.payload.includeOpinionOfAfterRepairValue ? ['as-is', 'subject-to'] : [];
      }

      Object.assign(state, {
        includeTotalCostToCure: action.payload.includeTotalCostToCure,
        opinionOfMarketValue: action.payload.opinionOfMarketValue,
        totalCostToCure: action.payload.totalCostToCure,
        opinionOfAfterRepairValue: action.payload.opinionOfAfterRepairValue,
        totalRenovationBudget: action.payload.totalRenovationBudget,
        includeTotalRenovationBudget: action.payload.includeTotalRenovationBudget,
        includeOpinionOfAfterRepairValue: action.payload.includeOpinionOfAfterRepairValue,
        allowSubjectToValue: action.payload.allowSubjectToValue ?? true,
        valueTypes,
        syncState: {
          editRevision: 0,
          serverRevision: 0,
          status: 'success',
        },
      });
    },
    reconciliationMutationComplete: (state, action: PayloadAction<SyncResult>) => {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.syncState)) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
    resetReconciliationValue: (
      state,
      action: PayloadAction<{ fieldName: string; resetValue: any }>,
    ) => {
      const { fieldName, resetValue } = action.payload;
      Object.assign(state, { [fieldName]: resetValue });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialReconciliationState);
    builder.addCase(submitEditOrderDetailsForm, (state, action) => {
      const { totalRenovationBudget } = action.payload;

      Object.assign(state, {
        totalRenovationBudget,
        syncState: getBumpedSyncState(state.syncState),
      });
    });
  },
});

export const {
  submitReconciliation,
  submitReconciliationDataSheet,
  reconciliationMutationComplete,
  setReconciliation,
  resetReconciliationValue,
} = reconciliationSlice.actions;
export const reconciliationReducer = reconciliationSlice.reducer;
